/**
 * Compress an image File to a JPEG/PNG data URL with a max width.
 * Drastically reduces memory footprint of menu images stored as base64.
 *
 * @param {File} file - Image file (jpg, png, webp...)
 * @param {Object} opts
 * @param {number} opts.maxWidth - Max width in px (default 1920)
 * @param {number} opts.maxHeight - Max height in px (default 1920)
 * @param {number} opts.quality - JPEG quality 0-1 (default 0.85)
 * @returns {Promise<{dataUrl: string, width: number, height: number, sizeKB: number}>}
 */
export const compressImage = (file, opts = {}) => {
  const { maxWidth = 1920, maxHeight = 1920, quality = 0.85 } = opts;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Failed to load image"));
      img.onload = () => {
        let { width, height } = img;
        // Scale down maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Use original format for PNG/transparent images, otherwise JPEG for size
        const isPng = file.type === "image/png";
        const mime = isPng ? "image/png" : "image/jpeg";
        const dataUrl = canvas.toDataURL(mime, isPng ? undefined : quality);
        const sizeKB = Math.round((dataUrl.length * 3) / 4 / 1024);
        resolve({ dataUrl, width, height, sizeKB });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};
